import {
    ActionType,
    CalendarDate,
    formatDateNoTime,
    FranchiseFeeStructureId,
    FranchiseFeeStructureMode,
    FranchiseTerritoryId,
    FranchiseTerritoryStatus,
    RegionId,
    rootRegionParentId,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { CalendarDateInput } from "components/ui/calendar-date";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { ReorderableStringList } from "components/ui/reorderable-string-list";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { useMessage } from "hooks/use-message";
import React from "react";
import { useSelector } from "react-redux";
import { useFeature, useIsAllowedInWholeTenant } from "store/selectors/hooks";
import { ApplicationState } from "store/state";

interface TerritoryFormData {
    name: string;
    code: string;
    status: FranchiseTerritoryStatus;
    zipCodes: string[];
    assignedRegionId: RegionId | "";
    purchaseDate: CalendarDate | null;
    trainingDate: CalendarDate | null;
}

interface ScheduleEntryFormData {
    _key: string;
    effectiveDate: string;
    amountDollars: string;
}

interface FeeStructureFormData {
    _key: string;
    existingId: FranchiseFeeStructureId | null;
    name: string;
    mode: FranchiseFeeStructureMode;
    feePercentDisplay: string;
    commencementDate: CalendarDate | null;
    scheduleEntries: ScheduleEntryFormData[];
}

interface ScheduleEntrySummary {
    effectiveDate: string;
    amountCents: number;
}

interface FeeStructureSummary {
    name: string;
    mode: FranchiseFeeStructureMode;
    feePercent?: number | null;
    currentMinimumCents?: number;
    scheduleEntries?: ScheduleEntrySummary[];
}

interface FeeStructureDetail {
    id: string;
    name: string;
    mode: FranchiseFeeStructureMode;
    feePercentBasisPoints?: number | null;
    commencementDate?: string | null;
    scheduleEntries: {
        id: string;
        effectiveDate: string;
        amountCents: number;
    }[];
}

interface TerritoryRow {
    id: FranchiseTerritoryId;
    name: string;
    code: string;
    status: FranchiseTerritoryStatus;
    zipCodes: string[];
    assignedRegionId?: RegionId | null;
    assignedRegionName?: string | null;
    purchaseDate?: string | null;
    trainingDate?: string | null;
    feeStructureSummaries: FeeStructureSummary[];
    feeStructures: FeeStructureDetail[];
    dateAdded: string;
    updatedAt: string;
}

interface TerritoryFormPanelProps {
    onDismiss: () => void;
    onSuccess: () => void;
    editingTerritory?: TerritoryRow;
    initialFeeStructures: FeeStructureFormData[];
}

let formKeyCounter = 0;
function nextFormKey(): string {
    return `fk-${++formKeyCounter}`;
}

function formatDollars(cents: number): string {
    return `$${(cents / 100).toLocaleString()}`;
}

function formatFeeStructureSummary(fs: FeeStructureSummary): string {
    const min = formatDollars(fs.currentMinimumCents ?? 0);
    if (fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum) {
        const pct = fs.feePercent != null ? `${fs.feePercent}%` : "0%";
        return `${fs.name}: ${pct} (${min} minimum)`;
    }
    return `${fs.name}: ${min}`;
}

function formatScheduleEntry(
    fs: FeeStructureSummary,
    entry: ScheduleEntrySummary,
): string {
    const fee = formatDollars(entry.amountCents);
    const date = formatDateNoTime(entry.effectiveDate);
    if (
        fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum &&
        fs.feePercent != null
    ) {
        return `${fs.feePercent}% (${fee} minimum) on ${date}`;
    }
    return `${fee} on ${date}`;
}

function futureScheduleEntries(
    fs: FeeStructureSummary,
    today: string,
): ScheduleEntrySummary[] {
    return (fs.scheduleEntries ?? []).filter(e => e.effectiveDate > today);
}

function parseCents(dollars: string): number {
    const val = parseFloat(dollars);
    if (isNaN(val)) return 0;
    return Math.round(val * 100);
}

function parseBasisPoints(percent: string): number {
    const val = parseFloat(percent);
    if (isNaN(val)) return 0;
    return Math.round(val * 100);
}

const TerritoryFormPanel: React.FC<TerritoryFormPanelProps> = props => {
    const { onDismiss, onSuccess, editingTerritory, initialFeeStructures } = props;

    const isEditing = !!editingTerritory;
    const [formData, setFormData] = React.useState<TerritoryFormData>(() =>
        editingTerritory
            ? {
                  name: editingTerritory.name,
                  code: editingTerritory.code,
                  status: editingTerritory.status,
                  zipCodes: editingTerritory.zipCodes,
                  assignedRegionId: editingTerritory.assignedRegionId ?? "",
                  purchaseDate: editingTerritory.purchaseDate
                      ? CalendarDate.parse(editingTerritory.purchaseDate).getOrElse(null)
                      : null,
                  trainingDate: editingTerritory.trainingDate
                      ? CalendarDate.parse(editingTerritory.trainingDate).getOrElse(null)
                      : null,
              }
            : {
                  name: "",
                  code: "",
                  status: FranchiseTerritoryStatus.Active,
                  zipCodes: [],
                  assignedRegionId: "",
                  purchaseDate: null,
                  trainingDate: null,
              },
    );
    const [feeStructures, setFeeStructures] =
        React.useState<FeeStructureFormData[]>(initialFeeStructures);
    const [originalFeeStructureIds] = React.useState(() =>
        initialFeeStructures.filter(fs => fs.existingId).map(fs => fs.existingId!),
    );
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const { message: panelMessage, setMessage: setPanelMessage } = useMessage();

    const regions = useSelector((state: ApplicationState) => state.region.regions);
    const childRegions = regions.filter(r => r.parentRegionId !== rootRegionParentId);

    const trpcClient = trpc.useTRPC();

    const createMutation = useMutation({
        ...trpcClient.franchiseTerritory.create.mutationOptions(),
    });

    const updateMutation = useMutation({
        ...trpcClient.franchiseTerritory.update.mutationOptions(),
    });

    const createFeeStructureMutation = useMutation({
        ...trpcClient.franchiseTerritory.createFeeStructure.mutationOptions(),
    });

    const updateFeeStructureMutation = useMutation({
        ...trpcClient.franchiseTerritory.updateFeeStructure.mutationOptions(),
    });

    const deleteFeeStructureMutation = useMutation({
        ...trpcClient.franchiseTerritory.deleteFeeStructure.mutationOptions(),
    });

    const updateFeeStructureField = React.useCallback(
        <K extends keyof FeeStructureFormData>(
            fsIndex: number,
            field: K,
            value: FeeStructureFormData[K],
        ) => {
            setFeeStructures(prev => {
                const updated = [...prev];
                updated[fsIndex] = { ...updated[fsIndex], [field]: value };
                return updated;
            });
        },
        [],
    );

    const updateScheduleEntry = React.useCallback(
        (
            fsIndex: number,
            entryIndex: number,
            field: keyof ScheduleEntryFormData,
            value: string,
        ) => {
            setFeeStructures(prev => {
                const updated = [...prev];
                const entries = [...updated[fsIndex].scheduleEntries];
                entries[entryIndex] = {
                    ...entries[entryIndex],
                    [field]: value,
                };
                updated[fsIndex] = {
                    ...updated[fsIndex],
                    scheduleEntries: entries,
                };
                return updated;
            });
        },
        [],
    );

    const addFeeStructure = React.useCallback(() => {
        setFeeStructures(prev => [
            ...prev,
            {
                _key: nextFormKey(),
                existingId: null,
                name: "",
                mode: FranchiseFeeStructureMode.PercentPlusMinimum,
                feePercentDisplay: "",
                commencementDate: null,
                scheduleEntries: [
                    {
                        _key: nextFormKey(),
                        effectiveDate: "",
                        amountDollars: "",
                    },
                ],
            },
        ]);
    }, []);

    const removeFeeStructure = React.useCallback((fsIndex: number) => {
        setFeeStructures(prev => prev.filter((_, i) => i !== fsIndex));
    }, []);

    const addScheduleEntry = React.useCallback((fsIndex: number) => {
        setFeeStructures(prev => {
            const updated = [...prev];
            updated[fsIndex] = {
                ...updated[fsIndex],
                scheduleEntries: [
                    ...updated[fsIndex].scheduleEntries,
                    {
                        _key: nextFormKey(),
                        effectiveDate: "",
                        amountDollars: "",
                    },
                ],
            };
            return updated;
        });
    }, []);

    const removeScheduleEntry = React.useCallback(
        (fsIndex: number, entryIndex: number) => {
            setFeeStructures(prev => {
                const updated = [...prev];
                updated[fsIndex] = {
                    ...updated[fsIndex],
                    scheduleEntries: updated[fsIndex].scheduleEntries.filter(
                        (_, i) => i !== entryIndex,
                    ),
                };
                return updated;
            });
        },
        [],
    );

    const validateFeeStructures = React.useCallback((): string | null => {
        for (let i = 0; i < feeStructures.length; i++) {
            const fs = feeStructures[i];
            if (!fs.name.trim()) {
                return `Fee structure #${i + 1}: Name is required.`;
            }
            if (
                fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum &&
                (!fs.feePercentDisplay.trim() || parseFloat(fs.feePercentDisplay) <= 0)
            ) {
                return `Fee structure "${fs.name || i + 1}": Fee percent is required and must be greater than 0.`;
            }
            if (fs.scheduleEntries.length === 0) {
                return `Fee structure "${fs.name || i + 1}": At least one fee schedule entry is required.`;
            }
            for (let j = 0; j < fs.scheduleEntries.length; j++) {
                const entry = fs.scheduleEntries[j];
                if (!entry.effectiveDate) {
                    return `Fee structure "${fs.name || i + 1}", entry #${j + 1}: Effective date is required.`;
                }
                if (!entry.amountDollars.trim() || parseFloat(entry.amountDollars) < 0) {
                    return `Fee structure "${fs.name || i + 1}", entry #${j + 1}: Amount is required.`;
                }
            }
        }
        return null;
    }, [feeStructures]);

    const buildFeeStructureInput = React.useCallback(
        (fs: FeeStructureFormData) => ({
            name: fs.name,
            mode: fs.mode,
            ...(fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum
                ? {
                      feePercentBasisPoints: parseBasisPoints(fs.feePercentDisplay),
                  }
                : {}),
            ...(fs.commencementDate
                ? { commencementDate: fs.commencementDate.toString() }
                : {}),
            scheduleEntries: fs.scheduleEntries.map(e => ({
                effectiveDate: e.effectiveDate,
                amountCents: parseCents(e.amountDollars),
            })),
        }),
        [],
    );

    const handleSubmit = React.useCallback(async () => {
        if (!formData.name.trim()) {
            setValidationError("Territory name is required.");
            return;
        }
        if (!formData.code.trim()) {
            setValidationError("Territory code is required.");
            return;
        }
        if (formData.zipCodes.length === 0) {
            setValidationError("At least one zip code is required.");
            return;
        }

        const fsError = validateFeeStructures();
        if (fsError) {
            setValidationError(fsError);
            return;
        }

        setValidationError(null);
        setIsSaving(true);

        try {
            if (isEditing && editingTerritory) {
                await updateMutation.mutateAsync({
                    id: editingTerritory.id,
                    name: formData.name,
                    code: formData.code,
                    status: formData.status,
                    zipCodes: formData.zipCodes,
                    assignedRegionId: formData.assignedRegionId || null,
                    purchaseDate: formData.purchaseDate?.toString() ?? null,
                    trainingDate: formData.trainingDate?.toString() ?? null,
                });

                const currentExistingIds = new Set(
                    feeStructures.filter(fs => fs.existingId).map(fs => fs.existingId!),
                );

                for (const id of originalFeeStructureIds) {
                    if (!currentExistingIds.has(id)) {
                        await deleteFeeStructureMutation.mutateAsync({
                            feeStructureId: id,
                        });
                    }
                }

                for (const fs of feeStructures.filter(f => !f.existingId)) {
                    await createFeeStructureMutation.mutateAsync({
                        territoryId: editingTerritory.id,
                        ...buildFeeStructureInput(fs),
                    });
                }

                for (const fs of feeStructures.filter(f => f.existingId)) {
                    await updateFeeStructureMutation.mutateAsync({
                        feeStructureId: fs.existingId!,
                        ...buildFeeStructureInput(fs),
                    });
                }
            } else {
                const created = await createMutation.mutateAsync({
                    name: formData.name,
                    code: formData.code,
                    status: formData.status,
                    zipCodes: formData.zipCodes,
                    ...(formData.assignedRegionId
                        ? { assignedRegionId: formData.assignedRegionId }
                        : {}),
                    ...(formData.purchaseDate
                        ? { purchaseDate: formData.purchaseDate.toString() }
                        : {}),
                    ...(formData.trainingDate
                        ? { trainingDate: formData.trainingDate.toString() }
                        : {}),
                });

                for (const fs of feeStructures) {
                    await createFeeStructureMutation.mutateAsync({
                        territoryId: created.id as FranchiseTerritoryId,
                        ...buildFeeStructureInput(fs),
                    });
                }
            }

            onSuccess();
            onDismiss();
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Failed to save.";
            setPanelMessage("error", message);
        } finally {
            setIsSaving(false);
        }
    }, [
        formData,
        isEditing,
        editingTerritory,
        feeStructures,
        originalFeeStructureIds,
        createMutation,
        updateMutation,
        createFeeStructureMutation,
        updateFeeStructureMutation,
        deleteFeeStructureMutation,
        buildFeeStructureInput,
        validateFeeStructures,
        onSuccess,
        onDismiss,
        setPanelMessage,
    ]);

    return (
        <Panel
            isOpen={true}
            headerText={isEditing ? "Edit Territory" : "Add Territory"}
            onDismiss={onDismiss}
            type={PanelType.large}>
            <div className="space-y-6">
                <fieldset className="border-b-2 border-gray-200 pb-6">
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Territory Details
                    </legend>
                    <div className="space-y-4">
                        <Input
                            label="Name"
                            value={formData.name}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                            disabled={isSaving}
                        />
                        <Input
                            label="Territory Code"
                            value={formData.code}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    code: e.target.value,
                                }))
                            }
                            disabled={isSaving}
                        />
                        <Select
                            label="Status"
                            value={formData.status}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    status: e.target.value as FranchiseTerritoryStatus,
                                }))
                            }
                            disabled={isSaving}>
                            <option value={FranchiseTerritoryStatus.Active}>
                                Active
                            </option>
                            <option value={FranchiseTerritoryStatus.Inactive}>
                                Inactive
                            </option>
                        </Select>
                        <Select
                            label="Assigned Region (optional)"
                            value={formData.assignedRegionId}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    assignedRegionId: e.target.value as RegionId | "",
                                }))
                            }
                            disabled={isSaving}>
                            <option value="">No region</option>
                            {childRegions.map(region => (
                                <option key={region.id} value={region.id}>
                                    {region.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                </fieldset>
                <fieldset className="border-b-2 border-gray-200 pb-6">
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Zip Codes
                    </legend>
                    <ReorderableStringList
                        label=""
                        initial={formData.zipCodes}
                        onCommit={values =>
                            setFormData(prev => ({
                                ...prev,
                                zipCodes: values,
                            }))
                        }
                        addButtonLabel="Add zip code"
                    />
                </fieldset>
                <fieldset className="border-b-2 border-gray-200 pb-6">
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Key Dates
                    </legend>
                    <div className="space-y-4">
                        <CalendarDateInput
                            label="Purchase Date"
                            value={formData.purchaseDate}
                            onChange={val =>
                                setFormData(prev => ({
                                    ...prev,
                                    purchaseDate: val,
                                }))
                            }
                        />
                        <CalendarDateInput
                            label="Training Date"
                            value={formData.trainingDate}
                            onChange={val =>
                                setFormData(prev => ({
                                    ...prev,
                                    trainingDate: val,
                                }))
                            }
                        />
                    </div>
                </fieldset>
                <fieldset>
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Fee Structures
                    </legend>
                    <div className="space-y-4">
                        {feeStructures.map((fs, fsIndex) => (
                            <div
                                key={fs._key}
                                className="border border-gray-200 rounded-md p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">
                                        {fs.name.trim() || `Fee Structure ${fsIndex + 1}`}
                                    </span>
                                    <DeleteButton
                                        backgroundColor="bg-white"
                                        size="sm"
                                        confirm={{
                                            title: "Delete Fee Structure?",
                                            message: `Remove "${fs.name || "this fee structure"}" and all its schedule entries?`,
                                            confirmLabel: "Delete",
                                        }}
                                        onClick={() => removeFeeStructure(fsIndex)}
                                    />
                                </div>
                                <Input
                                    label="Name"
                                    textSize="sm"
                                    value={fs.name}
                                    onChange={e =>
                                        updateFeeStructureField(
                                            fsIndex,
                                            "name",
                                            e.target.value,
                                        )
                                    }
                                    disabled={isSaving}
                                />
                                <Select
                                    label="Mode"
                                    textSize="sm"
                                    value={fs.mode}
                                    onChange={e => {
                                        const newMode = e.target
                                            .value as FranchiseFeeStructureMode;
                                        setFeeStructures(prev => {
                                            const updated = [...prev];
                                            updated[fsIndex] = {
                                                ...updated[fsIndex],
                                                mode: newMode,
                                                feePercentDisplay:
                                                    newMode ===
                                                    FranchiseFeeStructureMode.FlatFee
                                                        ? ""
                                                        : updated[fsIndex]
                                                              .feePercentDisplay,
                                            };
                                            return updated;
                                        });
                                    }}
                                    disabled={isSaving}>
                                    <option value={FranchiseFeeStructureMode.FlatFee}>
                                        Flat Fee
                                    </option>
                                    <option
                                        value={
                                            FranchiseFeeStructureMode.PercentPlusMinimum
                                        }>
                                        Percent + Minimum
                                    </option>
                                </Select>
                                {fs.mode ===
                                FranchiseFeeStructureMode.PercentPlusMinimum ? (
                                    <Input
                                        label="Monthly fee percent (%)"
                                        textSize="sm"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={fs.feePercentDisplay}
                                        onChange={e =>
                                            updateFeeStructureField(
                                                fsIndex,
                                                "feePercentDisplay",
                                                e.target.value,
                                            )
                                        }
                                        disabled={isSaving}
                                    />
                                ) : null}
                                <CalendarDateInput
                                    label="Commencement Date"
                                    textSize="sm"
                                    value={fs.commencementDate}
                                    onChange={val =>
                                        updateFeeStructureField(
                                            fsIndex,
                                            "commencementDate",
                                            val,
                                        )
                                    }
                                />
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">
                                        {fs.mode ===
                                        FranchiseFeeStructureMode.PercentPlusMinimum
                                            ? "Minimum fee schedule"
                                            : "Fee schedule"}
                                    </p>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr>
                                                <th className="text-left pb-1 pr-2 font-medium text-gray-600">
                                                    Effective Date
                                                </th>
                                                <th className="text-left pb-1 pr-2 font-medium text-gray-600">
                                                    {fs.mode ===
                                                    FranchiseFeeStructureMode.PercentPlusMinimum
                                                        ? "Monthly minimum ($)"
                                                        : "Monthly fee ($)"}
                                                </th>
                                                <th className="w-8"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fs.scheduleEntries.map((entry, eIdx) => (
                                                <tr key={entry._key}>
                                                    <td className="pr-2 pb-1">
                                                        <Input
                                                            type="date"
                                                            textSize="sm"
                                                            value={entry.effectiveDate}
                                                            onChange={e =>
                                                                updateScheduleEntry(
                                                                    fsIndex,
                                                                    eIdx,
                                                                    "effectiveDate",
                                                                    e.target.value,
                                                                )
                                                            }
                                                            disabled={isSaving}
                                                        />
                                                    </td>
                                                    <td className="pr-2 pb-1">
                                                        <Input
                                                            type="number"
                                                            textSize="sm"
                                                            step="0.01"
                                                            min="0"
                                                            value={entry.amountDollars}
                                                            onChange={e =>
                                                                updateScheduleEntry(
                                                                    fsIndex,
                                                                    eIdx,
                                                                    "amountDollars",
                                                                    e.target.value,
                                                                )
                                                            }
                                                            disabled={isSaving}
                                                        />
                                                    </td>
                                                    <td className="pb-1">
                                                        <DeleteButton
                                                            backgroundColor="bg-white"
                                                            size="sm"
                                                            onClick={() =>
                                                                removeScheduleEntry(
                                                                    fsIndex,
                                                                    eIdx,
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <SecondaryCompactButton
                                        onClick={() => addScheduleEntry(fsIndex)}
                                        disabled={isSaving}>
                                        Add Entry
                                    </SecondaryCompactButton>
                                </div>
                            </div>
                        ))}
                        <SecondaryButton onClick={addFeeStructure} disabled={isSaving}>
                            Add Fee Structure
                        </SecondaryButton>
                    </div>
                </fieldset>
                {validationError ? (
                    <InlineBanner type="error">{validationError}</InlineBanner>
                ) : null}
                {panelMessage ? (
                    <InlineBanner type={panelMessage.type}>
                        {panelMessage.content}
                    </InlineBanner>
                ) : null}
                <div className="flex space-x-2">
                    <PrimaryButton onClick={handleSubmit} disabled={isSaving}>
                        {isSaving ? "Saving..." : "Save"}
                    </PrimaryButton>
                    <SecondaryButton onClick={onDismiss} disabled={isSaving}>
                        Cancel
                    </SecondaryButton>
                </div>
            </div>
        </Panel>
    );
};

export const AgencySettingsFranchise: React.FC<unknown> = () => {
    const isEnabled = useFeature("franchiseManagement");
    const canCreate = useIsAllowedInWholeTenant(ActionType.CreateFranchiseTerritory);
    const canUpdate = useIsAllowedInWholeTenant(ActionType.UpdateFranchiseTerritory);
    const canDelete = useIsAllowedInWholeTenant(ActionType.DeleteFranchiseTerritory);

    const trpcClient = trpc.useTRPC();
    const {
        data: territories,
        isLoading,
        refetch,
    } = useQuery(trpcClient.franchiseTerritory.list.queryOptions());

    const { message, setMessage } = useMessage();

    const deleteMutation = useMutation({
        ...trpcClient.franchiseTerritory.delete.mutationOptions(),
        onSuccess: () => {
            refetch();
        },
        onError: (error: { message?: string }) => {
            setMessage("error", error.message || "Failed to delete territory.");
        },
    });

    const [showPanel, setShowPanel] = React.useState(false);
    const [panelKey, setPanelKey] = React.useState(0);
    const [editingTerritory, setEditingTerritory] = React.useState<
        TerritoryRow | undefined
    >(undefined);

    const initialFeeStructures: FeeStructureFormData[] = React.useMemo(() => {
        if (!editingTerritory) return [];
        return editingTerritory.feeStructures.map(fs => ({
            _key: fs.id,
            existingId: fs.id as FranchiseFeeStructureId,
            name: fs.name,
            mode: fs.mode,
            feePercentDisplay:
                fs.feePercentBasisPoints != null
                    ? (fs.feePercentBasisPoints / 100).toFixed(2)
                    : "",
            commencementDate: fs.commencementDate
                ? CalendarDate.parse(fs.commencementDate).getOrElse(null)
                : null,
            scheduleEntries: fs.scheduleEntries.map(e => ({
                _key: e.id,
                effectiveDate: e.effectiveDate,
                amountDollars: (e.amountCents / 100).toFixed(2),
            })),
        }));
    }, [editingTerritory]);

    const handleAdd = React.useCallback(() => {
        setEditingTerritory(undefined);
        setPanelKey(k => k + 1);
        setShowPanel(true);
    }, []);

    const handleEdit = React.useCallback((territory: TerritoryRow) => {
        setEditingTerritory(territory);
        setPanelKey(k => k + 1);
        setShowPanel(true);
    }, []);

    const handleDismiss = React.useCallback(() => {
        setShowPanel(false);
        setEditingTerritory(undefined);
    }, []);

    const handleSuccess = React.useCallback(() => {
        refetch();
    }, [refetch]);

    const MAX_VISIBLE_ZIPS = 5;

    const columns: CrmTableProps<TerritoryRow>["columns"] = React.useMemo(
        () => [
            {
                key: "name",
                header: "Name",
            },
            {
                key: "code",
                header: "Code",
            },
            {
                key: "status",
                header: "Status",
                renderCell: ({ record }) =>
                    record ? (
                        <Badge
                            value={
                                record.status === FranchiseTerritoryStatus.Active
                                    ? "Active"
                                    : "Inactive"
                            }
                            type={
                                record.status === FranchiseTerritoryStatus.Active
                                    ? "success"
                                    : "info"
                            }
                        />
                    ) : (
                        <></>
                    ),
            },
            {
                key: "assignedRegionName",
                header: "Assigned Region",
                renderCell: ({ record }) => <>{record?.assignedRegionName ?? ""}</>,
            },
            {
                key: "zipCodes",
                header: "Zip Codes",
                enableSorting: false,
                renderCell: ({ record }) => {
                    if (!record) return <></>;
                    const zips = record.zipCodes;
                    if (zips.length <= MAX_VISIBLE_ZIPS) {
                        return <span className="text-sm">{zips.join(", ")}</span>;
                    }
                    return (
                        <span className="text-sm">
                            {zips.slice(0, MAX_VISIBLE_ZIPS).join(", ")} +
                            {zips.length - MAX_VISIBLE_ZIPS} more
                        </span>
                    );
                },
            },
            {
                key: "feeStructureSummaries" as keyof TerritoryRow,
                header: "Current fee structures",
                enableSorting: false,
                renderCell: ({ record }) => {
                    if (!record || record.feeStructureSummaries.length === 0) {
                        return <span className="text-sm text-gray-400">None</span>;
                    }
                    const today = new Date().toISOString().slice(0, 10);
                    return (
                        <div className="text-sm leading-snug space-y-1">
                            {record.feeStructureSummaries.map(fs => {
                                const future = futureScheduleEntries(fs, today);
                                return (
                                    <div key={fs.name}>
                                        <div>{formatFeeStructureSummary(fs)}</div>
                                        {future.map(e => (
                                            <div
                                                key={e.effectiveDate}
                                                className="pl-2 text-xs text-gray-400">
                                                {formatScheduleEntry(fs, e)}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
            },
            ...((canUpdate || canDelete
                ? [
                      {
                          key: "_actions" as keyof TerritoryRow,
                          header: "",
                          enableSorting: false,
                          renderCell: ({
                              record,
                          }: {
                              record: TerritoryRow | undefined;
                          }) =>
                              record ? (
                                  <div className="flex items-center space-x-2">
                                      {canUpdate ? (
                                          <EditButton
                                              backgroundColor="bg-white"
                                              onClick={() => handleEdit(record)}
                                          />
                                      ) : null}
                                      {canDelete ? (
                                          <DeleteButton
                                              backgroundColor="bg-white"
                                              confirm={{
                                                  title: "Delete Territory?",
                                                  message: `Are you sure you want to delete territory "${record.code}"?`,
                                                  confirmLabel: "Delete",
                                              }}
                                              onClick={() =>
                                                  deleteMutation.mutate({
                                                      id: record.id,
                                                  })
                                              }
                                          />
                                      ) : null}
                                  </div>
                              ) : null,
                      },
                  ]
                : []) as CrmTableProps<TerritoryRow>["columns"]),
        ],
        [canUpdate, canDelete, handleEdit, deleteMutation],
    );

    if (!isEnabled || !canUpdate) {
        return null;
    }

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Franchise Management</ContentHeader>
                {isLoading ? (
                    <Spinner />
                ) : (
                    <div className="space-y-4">
                        {canCreate ? (
                            <div>
                                <PrimaryButton onClick={handleAdd}>
                                    Add Territory
                                </PrimaryButton>
                            </div>
                        ) : null}
                        {message ? (
                            <InlineBanner type={message.type}>
                                {message.content}
                            </InlineBanner>
                        ) : null}
                        {territories && territories.length > 0 ? (
                            <CrmTable columns={columns} data={territories} />
                        ) : (
                            <p className="text-sm text-gray-500">
                                No franchise territories have been created yet.
                            </p>
                        )}
                    </div>
                )}
            </ContentFull>
            {showPanel ? (
                <TerritoryFormPanel
                    key={panelKey}
                    onDismiss={handleDismiss}
                    onSuccess={handleSuccess}
                    editingTerritory={editingTerritory}
                    initialFeeStructures={initialFeeStructures}
                />
            ) : null}
        </div>
    );
};
